/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';

// CDS
import { Typography, breakpoints } from '@coursera/cds-core';

import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import type {
  HubTutorialFieldsFragment,
  TutorialHubQuery,
  TutorialHubQueryVariables,
} from 'bundles/tutorials/__generated__/contentfulTypes';
import TutorialHubCard from 'bundles/tutorials/components/TutorialHubCard';
import TutorialHubPlaceholder from 'bundles/tutorials/components/TutorialHubPlaceholder';
import { tutorialHubQuery } from 'bundles/tutorials/queries';

import _t from 'i18n!nls/tutorials';

type PropsFromGraphQL = {
  tutorials?: Array<HubTutorialFieldsFragment | null>;
  isLoading?: boolean;
};

type Props = PropsFromGraphQL;

const styles = {
  hubContainer: css`
    max-width: 868px;
    width: 100%;
    margin: 0 auto;
  `,
  header: () => css`
    margin: var(--cds-spacing-600) var(--cds-spacing-800);
    padding: 0 var(--cds-spacing-150);
    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-600) 0;
    }
  `,
};

export const TutorialHubPage = ({ tutorials, isLoading }: Props) => {
  if (isLoading) {
    return <TutorialHubPlaceholder />;
  }

  if (!tutorials || tutorials.length === 0) {
    return null;
  }

  // This page is available in English only, but putting these in _t() for future-proofing
  const metaTitle = _t('Free Programming Tutorials');
  const metaDescription = _t(
    'Our free online tutorials provide introductions to common programming issues with examples and interactive exercises to test your skills.'
  );

  return (
    <div className="rc-TutorialHubPage" data-e2e="tutorial-hub-page">
      <div css={styles.hubContainer}>
        <Typography variant="d2semibold" component="h1" css={styles.header} data-e2e="tutorial-hub-page-header">
          {_t('Programming Tutorials')}
        </Typography>
        <div>
          {tutorials.map((tutorial) => (
            <TutorialHubCard key={tutorial?.slug} {...tutorial} />
          ))}
        </div>
      </div>
      <CourseraMetatags
        metaNameAndProperties={{
          title: metaTitle,
          ogAndTwitterTitle: metaTitle,
          description: metaDescription,
          disableCourseraDescriptionSuffix: true,
        }}
        hrefLangOverrides={() => []}
      />
    </div>
  );
};

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      page: 'tutorial_hub',
    },
  })),
  graphql<{}, TutorialHubQuery, TutorialHubQueryVariables, PropsFromGraphQL>(tutorialHubQuery, {
    options: () => {
      return {
        // Only send a request through the Delivery API
        context: { clientName: 'contentfulGql' },
        errorPolicy: 'all',
      };
    },
    props: (response) => ({
      tutorials: response?.data?.tutorialsCollection?.items,
      isLoading: response.data?.loading,
    }),
  })
)(TutorialHubPage);
